import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { AlertController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, select, Store } from '@ngrx/store';

import {
  compact,
  first,
  has,
  isEmpty,
  isNil,
  isNull,
  isUndefined,
  last,
  map as lodashMap,
  split,
  trim
} from 'lodash-es';
import { forkJoin, from, of, withLatestFrom } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap } from 'rxjs/operators';

import { AddressEnum } from '../../enums/address';
import { NetSheetStateEnum } from '../../enums/net-sheet';
import { findOption } from '../../helpers/net-sheet';
import { AddressPipe } from '../../misc/pipes/address.pipe';
import { UsdCurrencyPipe } from '../../misc/pipes/usd-currency.pipe';
import { AdminPortalApiService } from '../../misc/services/adminPortalApi.service';
import { ReportApiService } from '../../misc/services/reportApi.service';
import { NetSheetFipsResponse, NetSheetNameValuePair, NetSheetResult, NetSheetState } from '../../typings/net-sheet';
import * as fromNetSheetActions from '../actions/net-sheet.actions';
import * as fromUserActions from '../actions/user.actions';
import * as fromNetSheetSelectors from '../selectors/net-sheet.selectors';
import * as fromOfficeSelectors from '../selectors/office.selectors';
import * as fromPropertyProfileSelectors from '../selectors/property-profile.selectors';
import * as fromSettingSelectors from '../selectors/setting.selectors';
import * as fromUserSelectors from '../selectors/user.selectors';

@Injectable()
export class NetSheetEffects {
  private actions$ = inject(Actions);
  private adminPortalApiService = inject(AdminPortalApiService);
  private alertController = inject(AlertController);
  private store = inject(Store);
  private addressPipe = inject(AddressPipe);
  private reportApiService = inject(ReportApiService);
  private usdCurrencyPipe = inject(UsdCurrencyPipe);

  loadInitialData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.loadInitialData),
      withLatestFrom(this.store.pipe(select(fromOfficeSelectors.selectNetSheetsEnabled))),
      filter(([, isNetSheetEnabled]) => isNetSheetEnabled),
      concatMap(() => [
        fromNetSheetActions.loadTransactionInformationLocationStates({
          payload: {
            isEscrow: false
          }
        }),
        fromNetSheetActions.loadTransactionInformationLocationStates({
          payload: {
            isEscrow: true
          }
        })
      ])
    )
  );

  loadTransactionInformationLocationPropertyStates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.loadTransactionInformationLocationStates),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectReferralCode))),
      filter(([, referralCode]) => !isNull(referralCode)),
      concatMap(
        ([
          {
            payload: { isEscrow }
          },
          referralCode
        ]) =>
          this.adminPortalApiService
            .sendRequest('GET', '/v2/net-sheet/states', {
              params: new HttpParams().set('referralCode', referralCode ?? '').set('isEscrow', isEscrow)
            })
            .pipe(
              switchMap((payload: NetSheetState[]) =>
                compact([
                  fromNetSheetActions.loadTransactionInformationLocationStatesSuccess({
                    payload: {
                      isEscrow,
                      states: payload
                    }
                  })
                ])
              ),
              catchError((error) =>
                of(fromNetSheetActions.loadTransactionInformationLocationStatesFailure({ payload: { error } }))
              )
            )
      )
    )
  );

  loadTransactionInformationLocationStatesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.loadTransactionInformationLocationStatesSuccess),
      filter(({ payload: { states } }) => states.length === 1),
      map(({ payload: { isEscrow, states } }) => ({
        isEscrow,
        state: first(states)
      })),
      filter(({ state }) => !isUndefined(state)),
      map(({ isEscrow, state }) =>
        fromNetSheetActions.loadTransactionInformationLocationCounties({
          payload: {
            isEscrow,
            state: (state as NetSheetState).value
          }
        })
      )
    )
  );

  updateTransactionInformationLocationStatePropertyCounties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.updateTransactionInformationLocation),
      map(({ payload: { transactionInformationPropertyState } }) => transactionInformationPropertyState),
      filter((state) => !isUndefined(state)),
      map((state) =>
        isNull(state)
          ? fromNetSheetActions.loadTransactionInformationLocationCountiesSuccess({
              payload: {
                counties: [],
                isEscrow: false
              }
            })
          : fromNetSheetActions.loadTransactionInformationLocationCounties({
              payload: {
                isEscrow: false,
                state
              }
            })
      )
    )
  );

  updateTransactionInformationLocationStateEscrowCounties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.updateTransactionInformationLocation),
      map(({ payload: { transactionInformationEscrowState } }) => transactionInformationEscrowState),
      filter((state) => !isUndefined(state)),
      map((state) =>
        isNull(state)
          ? fromNetSheetActions.loadTransactionInformationLocationCountiesSuccess({
              payload: {
                counties: [],
                isEscrow: true
              }
            })
          : fromNetSheetActions.loadTransactionInformationLocationCounties({
              payload: {
                isEscrow: true,
                state
              }
            })
      )
    )
  );

  loadTransactionInformationLocationCounties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.loadTransactionInformationLocationCounties),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectReferralCode))),
      filter(([, referralCode]) => !isNull(referralCode)),
      concatMap(
        ([
          {
            payload: { isEscrow, state }
          },
          referralCode
        ]) =>
          this.adminPortalApiService
            .sendRequest('GET', `/v2/net-sheet/states/${state}/counties`, {
              params: new HttpParams().set('referralCode', referralCode ?? '').set('isEscrow', isEscrow)
            })
            .pipe(
              map((payload: NetSheetNameValuePair[]) =>
                fromNetSheetActions.loadTransactionInformationLocationCountiesSuccess({
                  payload: {
                    counties: payload,
                    isEscrow
                  }
                })
              ),
              catchError((error) =>
                of(fromNetSheetActions.loadTransactionInformationLocationCountiesFailure({ payload: { error } }))
              )
            )
      )
    )
  );

  updateTransactionInformationLocationStatePropertyCities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.updateTransactionInformationLocation),
      map(({ payload: { transactionInformationPropertyCounty } }) => transactionInformationPropertyCounty),
      filter((county) => !isUndefined(county)),
      withLatestFrom(this.store.pipe(select(fromNetSheetSelectors.selectTransactionInformationLocationPropertyState))),
      filter(([, state]) => !isNull(state)),
      map(([county, state]) =>
        isNull(county)
          ? fromNetSheetActions.loadTransactionInformationLocationCitiesSuccess({
              payload: {
                cities: [],
                isEscrow: false
              }
            })
          : fromNetSheetActions.loadTransactionInformationLocationCities({
              payload: {
                county,
                isEscrow: false,
                state: state as NetSheetStateEnum
              }
            })
      )
    )
  );

  updateTransactionInformationLocationStateEscrowCities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.updateTransactionInformationLocation),
      map(({ payload: { transactionInformationEscrowCounty } }) => transactionInformationEscrowCounty),
      filter((county) => !isUndefined(county)),
      withLatestFrom(this.store.pipe(select(fromNetSheetSelectors.selectTransactionInformationLocationEscrowState))),
      filter(([, state]) => !isNull(state)),
      map(([county, state]) =>
        isNull(county)
          ? fromNetSheetActions.loadTransactionInformationLocationCitiesSuccess({
              payload: {
                cities: [],
                isEscrow: true
              }
            })
          : fromNetSheetActions.loadTransactionInformationLocationCities({
              payload: {
                county,
                isEscrow: true,
                state: state as NetSheetStateEnum
              }
            })
      )
    )
  );

  loadTransactionInformationLocationCities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.loadTransactionInformationLocationCities),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectReferralCode))),
      filter(([, referralCode]) => !isNull(referralCode)),
      concatMap(
        ([
          {
            payload: { county, isEscrow, state }
          },
          referralCode
        ]) =>
          this.adminPortalApiService
            .sendRequest('GET', `/v2/net-sheet/states/${state}/counties/${county}/cities`, {
              params: new HttpParams().set('referralCode', referralCode ?? '').set('isEscrow', isEscrow)
            })
            .pipe(
              map((payload: NetSheetNameValuePair[]) =>
                fromNetSheetActions.loadTransactionInformationLocationCitiesSuccess({
                  payload: {
                    cities: payload,
                    isEscrow
                  }
                })
              ),
              catchError((error) =>
                of(fromNetSheetActions.loadTransactionInformationLocationCitiesFailure({ payload: { error } }))
              )
            )
      )
    )
  );

  updateTransactionInformationLocationStatePropertyZips$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.updateTransactionInformationLocation),
      map(({ payload: { transactionInformationPropertyCity } }) => transactionInformationPropertyCity),
      filter((city) => !isUndefined(city)),
      withLatestFrom(
        this.store.pipe(select(fromNetSheetSelectors.selectTransactionInformationLocationPropertyCounty)),
        this.store.pipe(select(fromNetSheetSelectors.selectTransactionInformationLocationPropertyState))
      ),
      filter(([, county, state]) => !isNull(county) && !isNull(state)),
      map(([city, county, state]) =>
        isNull(city)
          ? fromNetSheetActions.loadTransactionInformationLocationZipsSuccess({
              payload: {
                zips: []
              }
            })
          : fromNetSheetActions.loadTransactionInformationLocationZips({
              payload: {
                city,
                county: county as string,
                state: state as NetSheetStateEnum
              }
            })
      )
    )
  );

  loadTransactionInformationLocationZips$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.loadTransactionInformationLocationZips),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectReferralCode))),
      filter(([, referralCode]) => !isNull(referralCode)),
      concatMap(
        ([
          {
            payload: { city, county, state }
          },
          referralCode
        ]) =>
          this.adminPortalApiService
            .sendRequest('GET', `/v2/net-sheet/states/${state}/counties/${county}/cities/${city}/zips`, {
              params: new HttpParams().set('referralCode', referralCode ?? '')
            })
            .pipe(
              map((payload: NetSheetNameValuePair[]) =>
                fromNetSheetActions.loadTransactionInformationLocationZipsSuccess({
                  payload: {
                    zips: payload
                  }
                })
              ),
              catchError((error) =>
                of(fromNetSheetActions.loadTransactionInformationLocationZipsFailure({ payload: { error } }))
              )
            )
      )
    )
  );

  updateTransactionInformationLocationStateEscrowBranches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.updateTransactionInformationLocation),
      map(({ payload: { transactionInformationEscrowCounty } }) => transactionInformationEscrowCounty),
      filter((county) => !isNil(county)),
      withLatestFrom(this.store.pipe(select(fromNetSheetSelectors.selectTransactionInformationLocationPropertyState))),
      filter(([, state]) => !isNull(state)),
      map(([county, state]) =>
        fromNetSheetActions.loadTransactionInformationLocationEscrowBranches({
          payload: {
            county,
            state: state as NetSheetStateEnum
          }
        })
      )
    )
  );

  loadTransactionInformationLocationEscrowBranches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.loadTransactionInformationLocationEscrowBranches),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectReferralCode))),
      filter(([, referralCode]) => !isNull(referralCode)),
      concatMap(
        ([
          {
            payload: { county, state }
          },
          referralCode
        ]) =>
          this.adminPortalApiService
            .sendRequest('GET', `/v2/net-sheet/states/${state}/counties/${county}/branches`, {
              params: new HttpParams().set('referralCode', referralCode ?? '')
            })
            .pipe(
              map((payload: NetSheetNameValuePair[]) =>
                fromNetSheetActions.loadTransactionInformationLocationEscrowBranchesSuccess({
                  payload: {
                    branches: payload
                  }
                })
              ),
              catchError((error) =>
                of(fromNetSheetActions.loadTransactionInformationLocationEscrowBranchesFailure({ payload: { error } }))
              )
            )
      )
    )
  );

  loadMobileUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUserActions.loadMobileUserSuccess),
      withLatestFrom(
        this.store.pipe(select(fromOfficeSelectors.selectNetSheetsEnabled)),
        this.store.pipe(select(fromUserSelectors.selectDetails))
      ),
      filter(([, isNetSheetEnabled]) => isNetSheetEnabled),
      map(([, , userDetails]) =>
        fromNetSheetActions.updateUserInformation({
          payload: {
            userInformationCompany: userDetails.company,
            userInformationEmail: userDetails.email,
            userInformationFirstName: userDetails.firstName,
            userInformationLastName: userDetails.lastName,
            userInformationPhone: userDetails.phone
          }
        })
      )
    )
  );

  prefillFromPropertyProfilePropertyLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.loadPropertyProfilePrefillInformationSuccess),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectReferralCode))),
      filter(([prefill, referralCode]) => !isNull(referralCode) && !isNull(prefill.payload.state)),
      concatMap(
        ([
          {
            payload: { city, county, state }
          },
          referralCode
        ]) =>
          this.adminPortalApiService
            .sendRequest('GET', `/v2/net-sheet/states`, {
              params: new HttpParams().set('referralCode', referralCode ?? '').set('isEscrow', false)
            })
            .pipe(
              concatMap((states: NetSheetNameValuePair[]) => {
                const actions: Action[] = [];
                const stateOption = findOption(states, `${state}`);

                if (!isUndefined(stateOption)) {
                  return this.adminPortalApiService
                    .sendRequest('GET', `/v2/net-sheet/states/${stateOption.value}/counties`, {
                      params: new HttpParams().set('referralCode', referralCode ?? '').set('isEscrow', false)
                    })
                    .pipe(
                      concatMap((counties: NetSheetNameValuePair[]) => {
                        actions.push(
                          fromNetSheetActions.loadTransactionInformationLocationCountiesSuccess({
                            payload: {
                              counties,
                              isEscrow: false
                            }
                          })
                        );
                        const countyOption = findOption(counties, `${county}`);
                        if (!isUndefined(county) && !isUndefined(countyOption)) {
                          return this.adminPortalApiService
                            .sendRequest(
                              'GET',
                              `/v2/net-sheet/states/${stateOption.value}/counties/${countyOption.value}/cities`,
                              {
                                params: new HttpParams().set('referralCode', referralCode ?? '').set('isEscrow', false)
                              }
                            )
                            .pipe(
                              concatMap((cities: NetSheetNameValuePair[]) => {
                                actions.push(
                                  fromNetSheetActions.loadTransactionInformationLocationCitiesSuccess({
                                    payload: {
                                      cities,
                                      isEscrow: false
                                    }
                                  })
                                );
                                const cityOption = findOption(cities, `${city}`);

                                if (!isUndefined(city) && !isUndefined(cityOption)) {
                                  return this.adminPortalApiService
                                    .sendRequest(
                                      'GET',
                                      `/v2/net-sheet/states/${stateOption.value}/counties/${countyOption.value}/cities/${cityOption.value}/zips`,
                                      {
                                        params: new HttpParams().set('referralCode', referralCode ?? '')
                                      }
                                    )
                                    .pipe(
                                      concatMap((zips: NetSheetNameValuePair[]) => {
                                        actions.push(
                                          fromNetSheetActions.loadTransactionInformationLocationZipsSuccess({
                                            payload: {
                                              zips
                                            }
                                          })
                                        );
                                        return actions;
                                      })
                                    );
                                } else {
                                  return from(actions);
                                }
                              })
                            );
                        } else {
                          return from(actions);
                        }
                      })
                    );
                } else {
                  return from([]);
                }
              })
            )
      )
    )
  );

  prefillFromPropertyProfileEscrowLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.loadPropertyProfilePrefillInformationSuccess),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectReferralCode))),
      filter(([prefill, referralCode]) => !isNull(referralCode) && !isNull(prefill.payload.state)),
      concatMap(
        ([
          {
            payload: { county, state }
          },
          referralCode
        ]) =>
          this.adminPortalApiService
            .sendRequest('GET', `/v2/net-sheet/states`, {
              params: new HttpParams().set('referralCode', referralCode ?? '').set('isEscrow', true)
            })
            .pipe(
              concatMap((states: NetSheetNameValuePair[]) => {
                const actions: Action[] = [];
                const stateOption = findOption(states, `${state}`);
                if (!isUndefined(stateOption)) {
                  return this.adminPortalApiService
                    .sendRequest('GET', `/v2/net-sheet/states/${stateOption.value}/counties`, {
                      params: new HttpParams().set('referralCode', referralCode ?? '').set('isEscrow', true)
                    })
                    .pipe(
                      concatMap((counties: NetSheetNameValuePair[]) => {
                        actions.push(
                          fromNetSheetActions.loadTransactionInformationLocationCountiesSuccess({
                            payload: {
                              counties,
                              isEscrow: true
                            }
                          })
                        );
                        const countyOption = findOption(counties, `${county}`);
                        if (!isUndefined(county) && !isUndefined(countyOption)) {
                          return forkJoin({
                            branches: this.adminPortalApiService.sendRequest(
                              'GET',
                              `/v2/net-sheet/states/${stateOption.value}/counties/${countyOption.value}/branches`,
                              {
                                params: new HttpParams().set('referralCode', referralCode ?? '')
                              }
                            ),
                            cities: this.adminPortalApiService.sendRequest(
                              'GET',
                              `/v2/net-sheet/states/${stateOption.value}/counties/${countyOption.value}/cities`,
                              {
                                params: new HttpParams().set('referralCode', referralCode ?? '').set('isEscrow', true)
                              }
                            )
                          }).pipe(
                            concatMap(
                              ({
                                branches,
                                cities
                              }: {
                                branches: NetSheetNameValuePair[];
                                cities: NetSheetNameValuePair[];
                              }) => {
                                actions.push(
                                  fromNetSheetActions.loadTransactionInformationLocationCitiesSuccess({
                                    payload: {
                                      cities,
                                      isEscrow: true
                                    }
                                  }),
                                  fromNetSheetActions.loadTransactionInformationLocationEscrowBranchesSuccess({
                                    payload: {
                                      branches
                                    }
                                  })
                                );
                                return actions;
                              }
                            )
                          );
                        } else {
                          return actions;
                        }
                      })
                    );
                } else {
                  return [];
                }
              })
            )
      )
    )
  );

  prefillFromPropertyProfileSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.loadPropertyProfilePrefillInformationSuccess),
      map(({ payload: { address, city, county, firstName, fullName, lastName, propertyTaxes, state, zip } }) =>
        fromNetSheetActions.prefillInformation({
          payload: {
            address,
            city,
            county,
            firstName,
            fullName,
            lastName,
            propertyTaxes,
            state,
            zip
          }
        })
      )
    )
  );

  prefillFromPropertyProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.loadPropertyProfilePrefillInformation),
      concatLatestFrom(({ payload: { id } }) => [
        this.store.pipe(select(fromSettingSelectors.selectReferralCode)),
        this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfile(id ?? '')))
      ]),
      filter(([, referralCode, profile]) => !isNull(referralCode) && !isUndefined(profile)),
      switchMap(([, referralCode, profile]) =>
        this.adminPortalApiService
          .sendRequest('GET', `/v2/net-sheet/fips/${profile?.fips ?? ''}`, {
            params: new HttpParams().set('referralCode', referralCode ?? '')
          })
          .pipe(
            map((payload: NetSheetFipsResponse) => {
              const city = this.addressPipe.transform(profile, AddressEnum.City);
              const address = this.addressPipe.transform(profile, AddressEnum.LineOne);
              const zip = this.addressPipe.transform(profile, AddressEnum.Zip);
              let ownerNames: string[] = [];
              if (profile?.PrimaryOwnerName) {
                ownerNames = lodashMap(split(profile?.PrimaryOwnerName, ',', 2), trim);
              }
              const propertyTaxes = this.usdCurrencyPipe.transform(profile?.TaxAmount, '', 2) as string;

              return fromNetSheetActions.loadPropertyProfilePrefillInformationSuccess({
                payload: {
                  ...(!isNil(profile?.PrimaryOwnerName) && {
                    fullName: profile.PrimaryOwnerName
                  }),
                  ...(!isEmpty(ownerNames) && {
                    lastName: first(ownerNames),
                    ...(ownerNames.length === 2 && {
                      firstName: last(ownerNames)
                    })
                  }),
                  ...(address !== '--' && {
                    address
                  }),
                  county: payload.county,
                  state: payload.state as NetSheetStateEnum,
                  ...(city !== '--' && {
                    city
                  }),
                  ...(zip !== '--' && {
                    zip
                  }),
                  ...(!isEmpty(propertyTaxes) && {
                    propertyTaxes
                  })
                }
              });
            }),
            catchError((error) =>
              of(fromNetSheetActions.loadPropertyProfilePrefillInformationFailure({ payload: { error } }))
            )
          )
      )
    )
  );

  loadCalculate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNetSheetActions.calculate),
      withLatestFrom(
        this.store.pipe(select(fromNetSheetSelectors.selectNetSheetCalculatorRequestData)),
        this.store.pipe(select(fromSettingSelectors.selectReferralCode))
      ),
      filter(([, calculatorRequestData, referralCode]) => !isNull(calculatorRequestData) && !isNull(referralCode)),
      switchMap(([, calculatorRequestData, referralCode]) =>
        this.adminPortalApiService
          .sendRequest('POST', '/v2/net-sheet', {
            body: {
              ...calculatorRequestData
            },
            params: new HttpParams().set('referralCode', referralCode ?? '')
          })
          .pipe(
            map((payload: NetSheetResult) =>
              fromNetSheetActions.calculateSuccess({
                payload: {
                  calculatorReport: payload
                }
              })
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                fromNetSheetActions.calculateFailure({
                  payload: {
                    error
                  }
                })
              )
            )
          )
      )
    )
  );

  loadExportReportPdf$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromNetSheetActions.emailExportPdf),
        withLatestFrom(
          this.store.pipe(select(fromNetSheetSelectors.selectNetSheetExportReportPdfBody)),
          this.store.pipe(select(fromNetSheetSelectors.selectCustomerInformationPartyType)),
          this.store.pipe(select(fromSettingSelectors.selectReferralCode))
        ),
        switchMap(
          ([
            {
              payload: { email }
            },
            data,
            partyType,
            referralCode
          ]) =>
            this.reportApiService
              .sendRequest('POST', '/v4/net-sheets/export/email', {
                body: {
                  data,
                  email
                },
                params: new HttpParams().set('referralCode', referralCode ?? ''),
                responseType: 'blob'
              })
              .pipe(
                catchError((error: HttpErrorResponse) =>
                  of(
                    fromNetSheetActions.emailExportPdfFailure({
                      payload: {
                        error
                      }
                    })
                  )
                )
              )
        )
      ),
    {
      dispatch: false
    }
  );

  errorAlerts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromNetSheetActions.calculateFailure, fromNetSheetActions.emailExportPdfFailure),
        concatMap(async ({ payload: { error } }) => {
          const alert = await this.alertController.create({
            backdropDismiss: false,
            buttons: [
              {
                role: 'cancel',
                text: 'Ok'
              }
            ],
            header: 'Oops',
            message: has(error, 'error.description') ? error.error.description : error.message
          });
          await alert.present();
        })
      ),
    {
      dispatch: false
    }
  );
}
